
import { useState, useEffect } from "react";

export type SortConfig<T> = {
  key: keyof T;
  direction: 'asc' | 'desc';
}

export const useSortData = <T extends Record<string, any>>(data: T[]) => {
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(null);
  const [sortedData, setSortedData] = useState<T[]>(data);

  useEffect(() => {
    if (sortConfig) {
      applySorting(data);
    } else {
      setSortedData(data);
    }
  }, [data, sortConfig]);

  const applySorting = (dataToSort: T[]) => {
    if (!sortConfig) return dataToSort;

    const sortedResults = [...dataToSort].sort((a, b) => {
      // Sort according to configuration
      const { key, direction } = sortConfig;
      
      // Handle null/undefined values
      if (a[key] === null || a[key] === undefined) return direction === 'asc' ? 1 : -1;
      if (b[key] === null || b[key] === undefined) return direction === 'asc' ? -1 : 1;
      
      // Sort by data type
      if (typeof a[key] === 'string' && typeof b[key] === 'string') {
        // Specifically check for date strings
        if (
          key.toString().includes('date') || 
          key === 'created_at' || 
          key === 'verified_at' || 
          key === 'updated_at' ||
          key === 'date_adhesion' ||
          key === 'date_signature' ||
          key === 'date_abonnement_newsletter' ||
          key === 'date_don'
        ) {
          try {
            return direction === 'asc' 
              ? new Date(a[key] as string).getTime() - new Date(b[key] as string).getTime()
              : new Date(b[key] as string).getTime() - new Date(a[key] as string).getTime();
          } catch {
            // Fallback to string comparison if date parsing fails
            return direction === 'asc' 
              ? (a[key] as string).localeCompare(b[key] as string) 
              : (b[key] as string).localeCompare(a[key] as string);
          }
        }
        
        // Regular string comparison
        return direction === 'asc' 
          ? (a[key] as string).localeCompare(b[key] as string) 
          : (b[key] as string).localeCompare(a[key] as string);
      }
      
      if (typeof a[key] === 'number' && typeof b[key] === 'number') {
        return direction === 'asc' 
          ? (a[key] as number) - (b[key] as number) 
          : (b[key] as number) - (a[key] as number);
      }
      
      if (typeof a[key] === 'boolean' && typeof b[key] === 'boolean') {
        return direction === 'asc' 
          ? (a[key] === b[key] ? 0 : a[key] ? -1 : 1)
          : (a[key] === b[key] ? 0 : a[key] ? 1 : -1);
      }
      
      return 0;
    });

    setSortedData(sortedResults);
    return sortedResults;
  };

  const sortData = (key: keyof T) => {
    let direction: 'asc' | 'desc' = 'asc';
    
    // If clicking on the same column, invert the direction
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };

  return {
    sortConfig,
    sortedData,
    sortData,
  };
};
